import equal from "fast-deep-equal";

import { Card, Divider, Header, Icon } from "semantic-ui-react";

import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import formatDate from "astrid-helpers/src/formatDate";

import Flex from "../../../../../components/Flex/Flex";
import useHasRole from "../../../../authorization/hooks/useHasRole";
import DeliveryTypesSelect from "../../../../distribution/components/DeliveryTypes/DeliveryTypesSelect";
import DistributionTargetDataForm from "../../../../distribution/components/DistributionTargetDataForm/DistributionTargetDataForm";
import Form from "../../../../forms/components/Form/Form";
import Button from "../../../../ui/components/Buttons/Button";
import Detail from "../../../../ui/components/Detail/Detail";

import ArticleEditDates from "./ArticleEditDates";

export default function ArticleEditChannel({ channel, publisher }) {
	const { t } = useTranslation();

	const { watch, setValue } = Form.useFormContext();

	const hasRole = useHasRole();
	const isSuperAdmin = hasRole("owner", "admin");

	const [open, setOpen] = useState(false);

	const channelIds = watch("channelIds");

	const isChannelActive = channelIds?.includes(channel.id);

	const channelData = watch(`channels.${channel.id}`);

	const toggleChannel = () => {
		const updatedChannelIds = isChannelActive
			? channelIds.filter((id) => id !== channel.id)
			: [...(channelIds || []), channel.id];

		// a takedown that has already passed would filter the channel out again on save,
		// enabling the channel cancels it so the article can be re-activated
		const { takedownDate } = channelData || {};

		if (!isChannelActive && takedownDate && takedownDate <= formatDate()) {
			setValue(`channels.${channel.id}.takedownDate`, null, { shouldDirty: true });
		}

		setValue("channelIds", updatedChannelIds, { shouldDirty: true });
	};

	const hasChannelData = Boolean(
		(channelData?.prices && channelData.prices.length > 0) ||
			(channelData?.countries && channelData.countries.length > 0 && !equal(channelData.countries, ["WORLD"])) ||
			channelData?.announcementDate ||
			channelData?.releaseDate ||
			channelData?.takedownDate,
	);

	return (
		<>
			<Card fluid attached="top">
				<Card.Content
					style={{
						cursor: "pointer",
						opacity: isChannelActive ? 1 : 0.4,
					}}
					onClick={() => setOpen((open) => !open)}
				>
					<Header
						as="h4"
						style={{
							display: "flex",
							alignItems: "center",
							whiteSpace: "nowrap",
						}}
					>
						{channel.name}

						{!isChannelActive && (
							<span style={{ marginLeft: 10, opacity: 0.5, fontWeight: 300 }}>
								{t("disabled", "Disabled")}
							</span>
						)}

						{!open && (
							<Flex justifyContent="flex-end">
								<Icon link name={"pencil"} fitted style={{ fontSize: "0.9em" }} />
							</Flex>
						)}
					</Header>

					{isChannelActive && hasChannelData && !open && (
						<>
							<Divider style={{ margin: "0 0 10px 0" }} />

							<Flex justifyContent="flex-start" gap={20}>
								{channelData?.announcementDate && (
									<Detail
										label={t("announcementDate", "Announcement date")}
										value={channelData.announcementDate}
									/>
								)}

								{channelData?.releaseDate && (
									<Detail label={t("releaseDate", "Release date")} value={channelData.releaseDate} />
								)}

								{channelData?.takedownDate && (
									<Detail
										label={t("takedownDate", "Takedown date")}
										value={channelData.takedownDate}
									/>
								)}
							</Flex>

							{channelData?.prices.length > 0 && (
								<Detail label={t("prices", "Prices")} style={{ marginTop: 5 }}>
									<Flex justifyContent="flex-start" gap={5}>
										{channelData.prices.map((price, index) => (
											<div key={index}>
												{`${price.amount} ${price.currency} (${
													price.type === "single" ? "Single" : "Loan"
												})`}
											</div>
										))}
									</Flex>
								</Detail>
							)}

							{channelData.countries.length > 0 && !equal(channelData.countries, ["WORLD"]) && (
								<Detail label={t("countries", "Countries")} style={{ marginTop: 5 }}>
									<Flex justifyContent="flex-start" gap={5}>
										{channelData.countries.length > 6
											? `${channelData.countries.slice(0, 6).join(", ")} +${
													channelData.countries.length - 6
											  }`
											: channelData.countries.join(", ")}
									</Flex>
								</Detail>
							)}
						</>
					)}
				</Card.Content>

				{open && (
					<>
						{isChannelActive && (
							<Card.Content>
								<Form.Group widths="equal">
									<ArticleEditDates channel={channel} />
								</Form.Group>
								<DeliveryTypesSelect
									name={`channels.${channel.id}.deliveryTypes`}
									disabled={!isSuperAdmin}
								/>

								<DistributionTargetDataForm
									name={`channels.${channel.id}`}
									channel={channel}
									publisher={publisher}
								/>
							</Card.Content>
						)}

						<Card.Content extra>
							<Button onClick={() => toggleChannel()} floated="right">
								{isChannelActive ? t("disable", "Disable") : t("enable", "Enable")}
							</Button>
						</Card.Content>
					</>
				)}
			</Card>
		</>
	);
}
